import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getReportCategories,
  getMyReportForPhone,
  submitReport,
  updateReport,
} from '../services/firestoreService';

function ReportModal({ phoneNumber, isOpen, onClose, onSuccess }) {
  const { user } = useAuth();
  const [comment, setComment] = useState('');
  const [categoryId, setCategoryId] = useState('spam-call');
  const [companyName, setCompanyName] = useState('');
  const [reportId, setReportId] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = getReportCategories();
  const isEditMode = Boolean(reportId);

  useEffect(() => {
    if (!isOpen || !user || !phoneNumber?.canonicalId) {
      return;
    }

    let cancelled = false;
    setLoadingReport(true);
    setError('');

    getMyReportForPhone(phoneNumber.canonicalId, user.uid)
      .then((existing) => {
        if (cancelled) return;
        if (existing) {
          setReportId(existing.id);
          setComment(existing.comment || '');
          setCategoryId(existing.categoryId || 'spam-call');
          setCompanyName(existing.companyName || '');
        } else {
          setReportId(null);
          setComment('');
          setCategoryId('spam-call');
          setCompanyName('');
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your existing report');
      })
      .finally(() => {
        if (!cancelled) setLoadingReport(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, user, phoneNumber?.canonicalId]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const category = categories.find((c) => c.id === categoryId);
      const payload = {
        phoneNumberId: phoneNumber.canonicalId,
        comment,
        categoryId,
        categoryName: category?.name || 'Spam Call',
        ...(categoryId === 'legitimate-business' && companyName.trim()
          ? { companyName: companyName.trim() }
          : {}),
      };

      if (isEditMode) {
        await updateReport({ reportId, ...payload });
      } else {
        await submitReport(payload);
      }

      setComment('');
      setCompanyName('');
      setReportId(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err.code === 'already-exists' && err.existingReportId) {
        setReportId(err.existingReportId);
        setError('You already reported this number. Update your report below and save.');
      } else {
        setError(err.message || 'Failed to save report');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold mb-4">
          {isEditMode ? 'Edit your report' : 'Report this number'}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {isEditMode
            ? 'Changes are saved immediately on the public listing (unless an admin has hidden your report).'
            : 'Your report is published immediately. You can only submit one report per number.'}
        </p>

        {loadingReport ? (
          <p className="text-sm text-gray-500 mb-4">Loading your report…</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {categoryId === 'legitimate-business' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company / organization name (optional)
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  maxLength={120}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g. Commonwealth Bank, debt recovery agency"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comment
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required
                rows={4}
                className="w-full border rounded px-3 py-2"
                placeholder="Describe your experience with this number..."
              />
            </div>

            {['spam-call', 'scam', 'sales', 'robocall', 'harassment'].includes(categoryId) && (
              <p className="text-sm text-gray-600">
                Spam, sales, and unwanted calls affect nuisance scores. Scam and harassment
                reports trigger the strongest warnings.
              </p>
            )}

            {categoryId === 'private-individual' && (
              <p className="text-sm text-gray-600">
                This category can mark the listing as a private individual. Report
                comments may stay hidden publicly; scores still reflect community input.
              </p>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || loadingReport}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {loading
                  ? 'Saving...'
                  : isEditMode
                    ? 'Save changes'
                    : 'Submit report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ReportModal;
